import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../ScoreRanges.css';

const API_KEY     = import.meta.env.VITE_API_KEY || '';
const CURRENT_YEAR = new Date().getFullYear();

const CHAMP_OPTIONS = [
  { key: 'open', label: 'Open Class Championship' },
  { key: 'FE',   label: 'Forgotten Era Championships' },
  { key: 'VC',   label: 'Vintage/Classic Championship' },
  { key: 'RL',   label: 'Road Legal Championship' },
  { key: 'TT',   label: 'Top Ten Championship' },
];
const VALID_CHAMPS = new Set(CHAMP_OPTIONS.map(o => o.key));

// Mirrors each championship's class_order in backend/results_routes.py CHAMP_CONFIGS —
// kept static (not derived from the fetched response) so the class picker still shows
// every option even once a specific class filter has narrowed the response down to one.
// FEO isn't its own top-level championship here — it's already reachable as a sub-class
// filter under FE (class=FEO), so a standalone FEO option would be redundant.
const CLASSES_BY_CHAMP = {
  open: ['250', '350', '500', '750', 'HGV', 'Sidecar', 'Trike'],
  FE:   ['FEO', 'FEUNDER400', 'FEOVER400', 'FESIDECAR'],
  VC:   ['VC'],
  RL:   ['RL'],
  TT:   ['TT'],
};

// Classes with no record-bonus points at all (mirrors bonus=0 entries in
// backend _MAX_EVENT_POINTS) — ceiling_no_records and ceiling_with_records
// are always identical for these, so the records toggle/legend/column would
// just be a no-op reference and are hidden rather than shown-but-inert.
const NO_RECORD_BONUS_CLASSES = new Set(['FEO', 'RL', 'VC', 'TT']);

// Round step for axis ticks — 1/2/5 × a power of 10, aiming for ~5 ticks.
function niceStep(max) {
  const rough = Math.max(max, 1) / 5;
  const mag   = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm  = rough / mag;
  const step  = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return step * mag;
}

export default function ScoreRanges() {
  const [searchParams, setSearchParams] = useSearchParams();
  const season      = CURRENT_YEAR;
  const rawChamp    = searchParams.get('champ');
  const champ       = VALID_CHAMPS.has(rawChamp) ? rawChamp : 'open';
  const classFilter = searchParams.get('class') || '';
  const [showRecords, setShowRecords] = useState(false);

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  function setParam(key, val) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val) next.set(key, val); else next.delete(key);
      return next;
    });
  }

  // Sets champ and clears the class filter together, in one setSearchParams
  // call — two separate setParam() calls each close over the same stale
  // `searchParams` snapshot (React Router's setter isn't a chained/functional
  // update like useState's), so the second call's navigate() was clobbering
  // the first, making the championship dropdown appear to do nothing.
  function setChamp(newChamp) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('champ', newChamp);
      next.delete('class');
      return next;
    });
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    const qs = new URLSearchParams({ season, champ });
    if (classFilter) qs.set('class', classFilter);
    fetch(`/api/results/score-ranges?${qs.toString()}`, {
      headers: { 'X-API-Key': API_KEY },
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [season, champ, classFilter]);

  return (
    <div className="score-ranges-page">
      <h1>Possible Final Score Ranges</h1>
      <p className="sr-intro">
        {season} season — each rider&apos;s current score (guaranteed — already-run events can&apos;t
        get worse) up to their theoretical maximum if they win every remaining event.
      </p>

      <div className="sr-controls">
        <label>
          Championship
          <select value={champ} onChange={e => setChamp(e.target.value)}>
            {CHAMP_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </label>
        {CLASSES_BY_CHAMP[champ].length > 1 && (
          <label>
            Class
            <select value={classFilter} onChange={e => setParam('class', e.target.value)}>
              <option value="">All classes</option>
              {CLASSES_BY_CHAMP[champ].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        )}
        {data && data.classes.some(c => !NO_RECORD_BONUS_CLASSES.has(c.name)) && (
          <label className="sr-toggle">
            <input type="checkbox" checked={showRecords} onChange={e => setShowRecords(e.target.checked)} />
            Assume a record is broken at every remaining event
          </label>
        )}
      </div>

      {loading && <p className="sr-status">Loading…</p>}
      {error   && <p className="sr-status sr-error">{error}</p>}

      {data && (
        <p className="sr-subtitle">
          {data.label} · best {data.best_n} results count ·{' '}
          {data.remaining_events} event{data.remaining_events === 1 ? '' : 's'} remaining
        </p>
      )}

      {data && data.classes.map(cls => (
        <ClassRangeChart
          key={cls.name}
          cls={cls}
          showRecords={showRecords}
          remaining={data.remaining_events}
        />
      ))}

      {data && data.classes.length === 0 && (
        <p className="sr-status">No scored riders yet for this championship in {season}.</p>
      )}
    </div>
  );
}

function ClassRangeChart({ cls, showRecords, remaining }) {
  const riders = cls.riders;
  if (riders.length === 0) return null;

  const hasRecordBonus = !NO_RECORD_BONUS_CLASSES.has(cls.name);
  const maxCeiling = Math.max(...riders.map(r => r.ceiling_with_records));
  const scaleMax   = Math.max(1, maxCeiling) * 1.08;
  const pct        = v => `${(v / scaleMax) * 100}%`;

  const step  = niceStep(maxCeiling);
  const ticks = [];
  for (let t = 0; t <= maxCeiling + step / 2; t += step) ticks.push(Math.round(t));

  return (
    <section className="sr-class-block">
      <h2>{cls.name}</h2>

      <div className="sr-legend">
        <span className="sr-swatch sr-swatch-realistic" /> now → win every remaining event
        {hasRecordBonus && remaining > 0 && (
          <>
            <span className="sr-swatch sr-swatch-record" /> + break a record every time too
          </>
        )}
      </div>

      <div className="sr-chart">
        <div className="sr-gridlines">
          {ticks.map(t => (
            <span key={t} className="sr-gridline" style={{ left: pct(t) }}>
              <span className="sr-gridline-label">{t}</span>
            </span>
          ))}
        </div>

        {riders.map(r => {
          const noRecEnd   = r.ceiling_no_records;
          const withRecEnd = r.ceiling_with_records;
          const shownEnd    = showRecords ? withRecEnd : noRecEnd;
          return (
            <div className="sr-row" key={r.rider}>
              <div className="sr-rider-label">
                <span className="sr-rank">{r.rank}</span> {r.rider}
              </div>
              <div className="sr-track">
                <div
                  className="sr-bar sr-bar-realistic"
                  style={{ left: pct(r.best_n), width: pct(Math.max(noRecEnd - r.best_n, 0)) }}
                />
                {showRecords && withRecEnd > noRecEnd && (
                  <div
                    className="sr-bar sr-bar-record"
                    style={{ left: pct(noRecEnd), width: pct(withRecEnd - noRecEnd) }}
                  />
                )}
                <span className="sr-value sr-value-floor" style={{ left: pct(r.best_n) }}>
                  {r.best_n}
                </span>
                <span className="sr-value sr-value-ceiling" style={{ left: pct(shownEnd) }}>
                  {shownEnd}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <table className="sr-table">
        <caption>Table view</caption>
        <thead>
          <tr>
            <th>Rank</th><th>Rider</th><th>Now (floor)</th>
            <th>{hasRecordBonus ? 'Max, no records' : 'Max'}</th>
            {hasRecordBonus && <th>Max, with records</th>}
          </tr>
        </thead>
        <tbody>
          {riders.map(r => (
            <tr key={r.rider}>
              <td>{r.rank}</td>
              <td>{r.rider}</td>
              <td>{r.best_n}</td>
              <td>{r.ceiling_no_records}</td>
              {hasRecordBonus && <td>{r.ceiling_with_records}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
